import React, {
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
} from 'react';

import SunEditor from 'suneditor-react';
import 'suneditor/dist/css/suneditor.min.css';

const MyEditor = forwardRef(({ setContent, initialContent }, ref) => {
  const editorRef = useRef(null);
  const previousContentRef = useRef(initialContent);

  // Expose editor methods to parent
  useImperativeHandle(
    ref,
    () => ({
      setContents: (content) => {
        if (editorRef.current) {
          editorRef.current.setContents(content || '');
          focusEditor();
        }
      },
    }),
    []
  );

  // Update editor when initial content changes
  useEffect(() => {
    const editor = editorRef.current;

    if (
      editor &&
      initialContent !== previousContentRef.current
    ) {
      editor.setContents(initialContent || '');
      previousContentRef.current = initialContent;
    }
  }, [initialContent]);

  // Update parent content
  const handleChange = (content) => {
    if (content !== previousContentRef.current) {
      setContent(content);
      previousContentRef.current = content;
    }
  };

  const focusEditor = () => {
    if (editorRef.current?.editor) {
      editorRef.current.editor.focus();
    }
  };

  return (
    <div className="glass-scope my-glass-editor">
      <style>
        {`
          /* ===== Glass SunEditor ===== */

          .my-glass-editor {
            width: 100%;
            min-width: 0;
            margin-top: 12px;
            color: var(--g-text, #f5f0e8);
          }

          .my-glass-editor .sun-editor {
            overflow: hidden;
            border: 1px solid rgba(203, 158, 83, 0.24) !important;
            border-radius: 14px !important;
            background: rgba(18, 18, 18, 0.78) !important;
            box-shadow:
              inset 0 1px 0 rgba(255, 255, 255, 0.04),
              0 12px 32px rgba(0, 0, 0, 0.30) !important;
            backdrop-filter: blur(18px);
            -webkit-backdrop-filter: blur(18px);
            transition:
              border-color 0.25s ease,
              box-shadow 0.25s ease;
          }

          .my-glass-editor .sun-editor:focus-within {
            border-color: rgba(203, 158, 83, 0.65) !important;
            box-shadow:
              0 0 0 2px rgba(203, 158, 83, 0.08),
              0 12px 32px rgba(0, 0, 0, 0.35) !important;
          }

          /* Toolbar */
          .my-glass-editor .se-toolbar {
            background: rgba(25, 25, 25, 0.96) !important;
            border-bottom: 1px solid rgba(203, 158, 83, 0.20) !important;
            outline: none !important;
          }

          .my-glass-editor .se-btn-tray {
            background: transparent !important;
          }

          .my-glass-editor .se-btn-module-border {
            border-color: rgba(203, 158, 83, 0.12) !important;
          }

          .my-glass-editor .se-btn {
            color: #ded6c8 !important;
            background: transparent !important;
            border-color: transparent !important;
            border-radius: 6px !important;
            transition:
              color 0.2s ease,
              background 0.2s ease;
          }

          .my-glass-editor .se-btn:hover,
          .my-glass-editor .se-btn:focus {
            color: #d4a44b !important;
            background: rgba(203, 158, 83, 0.12) !important;
          }

          .my-glass-editor .se-btn:enabled.on {
            color: #d4a44b !important;
            background: rgba(203, 158, 83, 0.16) !important;
          }

          .my-glass-editor .se-btn:disabled {
            opacity: 0.35;
          }

          /* Toolbar icons */
          .my-glass-editor .se-btn svg,
          .my-glass-editor .se-btn svg path {
            fill: currentColor;
          }

          /* Editing area */
          .my-glass-editor .se-wrapper,
          .my-glass-editor .se-wrapper-inner,
          .my-glass-editor .sun-editor-editable {
            background: rgba(13, 13, 13, 0.92) !important;
            color: #f0ece5 !important;
          }

          .my-glass-editor .sun-editor-editable {
            box-sizing: border-box;
            padding: 22px !important;
            font-family: 'Sora', sans-serif;
            font-size: 14px;
            line-height: 1.8;
          }

          .my-glass-editor .sun-editor-editable p,
          .my-glass-editor .sun-editor-editable li,
          .my-glass-editor .sun-editor-editable span {
            color: inherit;
          }

          .my-glass-editor .sun-editor-editable a {
            color: #d4a44b !important;
          }

          .my-glass-editor .sun-editor-editable blockquote {
            border-left: 3px solid #cb9e53;
            padding-left: 14px;
            color: #c5bba9;
          }

          /* Placeholder */
          .my-glass-editor .sun-editor-editable[contenteditable="true"]:empty::before {
            color: #77716a !important;
          }

          /* Bottom status bar */
          .my-glass-editor .se-resizing-bar {
            background: rgba(25, 25, 25, 0.96) !important;
            border-top: 1px solid rgba(203, 158, 83, 0.16) !important;
            color: #a9a092 !important;
          }

          .my-glass-editor .se-char-counter-wrapper {
            color: #a9a092 !important;
          }

          /* Dropdowns and toolbar popups */
          .my-glass-editor .se-list-layer,
          .my-glass-editor .se-menu-list,
          .my-glass-editor .se-dialog,
          .my-glass-editor .se-controller {
            background: #1c1b19 !important;
            color: #f0ece5 !important;
            border: 1px solid rgba(203, 158, 83, 0.24) !important;
            border-radius: 10px !important;
            box-shadow: 0 12px 30px rgba(0, 0, 0, 0.45) !important;
          }

          .my-glass-editor .se-list-layer button,
          .my-glass-editor .se-menu-list li,
          .my-glass-editor .se-dialog button {
            color: #f0ece5 !important;
          }

          .my-glass-editor .se-list-layer button:hover,
          .my-glass-editor .se-menu-list li:hover {
            background: rgba(203, 158, 83, 0.12) !important;
            color: #d4a44b !important;
          }

          /* Scrollbar */
          .my-glass-editor .sun-editor-editable::-webkit-scrollbar {
            width: 7px;
          }

          .my-glass-editor .sun-editor-editable::-webkit-scrollbar-track {
            background: #111111;
          }

          .my-glass-editor .sun-editor-editable::-webkit-scrollbar-thumb {
            background: rgba(203, 158, 83, 0.38);
            border-radius: 10px;
          }

          .my-glass-editor .sun-editor-editable::-webkit-scrollbar-thumb:hover {
            background: rgba(203, 158, 83, 0.65);
          }

          /* Responsive */
          @media (max-width: 768px) {
            .my-glass-editor .sun-editor-editable {
              padding: 14px !important;
              font-size: 13px;
            }

            .my-glass-editor .se-toolbar {
              overflow-x: auto;
            }
          }
        `}
      </style>

      <SunEditor
        getSunEditorInstance={(sunEditor) => {
          editorRef.current = sunEditor;

          // Synchronize initial content with the editor
          const content = initialContent || '';
          sunEditor.setContents(content);
          previousContentRef.current = content;
        }}
        defaultValue={initialContent || ''}
        onChange={handleChange}
        height="500px"
        setOptions={{
          buttonList: [
            ['undo', 'redo', 'font', 'fontSize', 'formatBlock'],
            [
              'bold',
              'underline',
              'italic',
              'strike',
              'subscript',
              'superscript',
              'removeFormat',
            ],
            [
              'fontColor',
              'hiliteColor',
              'outdent',
              'indent',
              'align',
              'horizontalRule',
              'list',
              'table',
            ],
            [
              'link',
              'image',
              'video',
              'fullScreen',
              'showBlocks',
              'codeView',
              'preview',
              'print',
              'save',
            ],
          ],
        }}
      />
    </div>
  );
});

MyEditor.displayName = 'MyEditor';

export default MyEditor;